import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuItemMui from "@mui/material/MenuItem";
import { Add, DeleteForever } from "@mui/icons-material";
import { tokens } from "../../theme";
import dayjs, { utc } from "dayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";

const FieldDensityReportDetails = ({ mainData, setMainData, isNonMobile }) => {
  const [rows, setRows] = useState([]);
  const [samplingCount, setSamplingCount] = useState(0);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  // Initial row data structure
  const initialRow = {
    count: samplingCount,
    description: "",
    mdd: "",
    omc: "",
    dd: "",
    mc: "",
    degreeOfCompaction: "",
    layerThickness: "",
  };

  // GRAVEL , EARTH  , A.B.C
  const reportTypeArray = [
    {
      value: "GRAVEL",
      label: "GRAVEL",
    },
    {
      value: "EARTH",
      label: "EARTH",
    },
    {
      value: "A.B.C",
      label: "A.B.C",
    },
    {
      value: "SUB GRADE",
      label: "SUB GRADE",
    },
    {
      value: "QUARRY DUST",
      label: "QUARRY DUST",
    },
  ];
  useEffect(() => {
    setMainData((pre) => ({
      ...pre,
      labTestsDataFromSubs: rows,
    }));
  }, [rows]);

  // Add new row
  const addRow = () => {
    const newCount =
      mainData.labTestsDataFromSubs.length > 0
        ? Math.max(...mainData.labTestsDataFromSubs.map((row) => row.count)) + 1
        : 1;
    setSamplingCount(newCount);
    setRows([
      ...mainData.labTestsDataFromSubs,
      { ...initialRow, count: newCount },
    ]);
  };

  // Remove row
  const removeRow = (count) => {
    console.log("remov ", count);
    setSamplingCount((pre) => pre - 1);
    setRows(mainData.labTestsDataFromSubs.filter((row) => row.count !== count));
  };

  const degreeOfCompactionVal = (field, ddValue, mddValue) => {
    if (mddValue === "" || mddValue === 0) {
      return 0;
    }

    return (ddValue / mddValue).toFixed(2);
  };

  // Handle input changes
  const handleInputChange = (count, field, value) => {
    const updatedRows = mainData.labTestsDataFromSubs.map((row) => {
      if (row.count === count) {
        const updatedRow = { ...row, [field]: value };

        if (field === "dd" || field === "mdd") {
          const ddValue = field === "dd" ? value : updatedRow.dd;
          const mddValue = field === "mdd" ? value : updatedRow.mdd;

          // Calculate only if both values are valid numbers
          if (ddValue && mddValue && !isNaN(ddValue) && !isNaN(mddValue)) {
            const degreeOfCompaction =
              (parseFloat(ddValue) / parseFloat(mddValue)) * 100;
            updatedRow.degreeOfCompaction = degreeOfCompaction.toFixed(2);
          } else {
            updatedRow.degreeOfCompaction = "";
          }
        }
        return updatedRow;
      }
      return row;
    });

    setRows(updatedRows);
  };

  return (
    <Box>
      <Box>
        <Box display="flex" flexDirection="row" gap={1}>
          {" "}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer
              components={["DatePicker"]}
              sx={{
                paddingTop: "8px",
                paddingBottom: "8px",
                marginLeft: "15px",
              }}
            >
              <DatePicker
                label="Date of site visit"
                size="small"
                format="YYYY-MM-DD"
                timezone="Asia/Colombo"
                value={
                  mainData.dateOfSiteVisit
                    ? dayjs(mainData.dateOfSiteVisit).tz("Asia/Colombo", true)
                    : dayjs(new Date()).tz("Asia/Colombo", true)
                }
                onChange={(newValue) => {
                  const colomboTime = newValue?.tz("Asia/Colombo", true);
                  setMainData((pre) => ({
                    ...pre,
                    dateOfSiteVisit: dayjs(colomboTime).format("YYYY-MM-DD"),
                  }));
                }}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    size: "small",
                  },
                }}
              />
            </DemoContainer>
          </LocalizationProvider>
          <TextField
            select
            label="Report Type"
            size="small"
            defaultValue=""
            value={mainData.grade}
            onChange={(e) => {
              setMainData((pre) => ({
                ...pre,
                grade: e.target.value,
              }));
            }}
            sx={{
              marginTop: "7px",
              width: "150px",
            }}
          >
            {reportTypeArray?.map((option) => (
              <MenuItemMui key={option.value} value={option.value}>
                {option.label}
              </MenuItemMui>
            ))}
          </TextField>
        </Box>
        <Button
          type="submit"
          color="info"
          startIcon={<Add />}
          variant="contained"
          onClick={addRow}
          sx={{ marginBottom: "15px", marginLeft: "15px" }}
        >
          Add Data
        </Button>
        {mainData.labTestsDataFromSubs.map((row, index) => (
          <Box flexDirection={row} display="flex" gap={0.5} key={index}>
            <Typography
              variant="h5"
              justifyContent="center"
              alignContent="center"
            >
              {row.count}.
            </Typography>
            <TextField
              variant="outlined"
              sx={{ margin: "2px 0 5px 0" }}
              type="text"
              label="Description"
              size="small"
              value={row.description}
              fullWidth
              onChange={(e) =>
                handleInputChange(row.count, "description", e.target.value)
              }
            />
            <IconButton onClick={() => removeRow(row.count)} color="error">
              <DeleteForever sx={{ fontSize: "25px" }} />
            </IconButton>
          </Box>
        ))}
      </Box>
      <Divider sx={{ margin: "5px 0 5px 0" }} />
      {/* -----------------------------------------------------------------enter values---------------------------------- */}
      <Box>
        {mainData.labTestsDataFromSubs.map((row, index) => (
          <Box key={index}>
            <Box
              display="grid"
              gap={0.5}
              padding="2px"
              gridTemplateColumns="repeat(7, minmax(0, 1fr))"
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 7",
                  color: "red",
                },
              }}
            >
              <Typography
                sx={{ gridColumn: "span 1" }}
                textAlign="center"
                variant="subtitle2"
                color="info"
              >
                Location No
              </Typography>
              <Typography
                sx={{ gridColumn: "span 1" }}
                textAlign="center"
                variant="subtitle2"
                color="info"
              >
                M.D.D. (g/cm³)
              </Typography>
              <Typography
                sx={{ gridColumn: "span 1" }}
                textAlign="center"
                variant="subtitle2"
                color="info"
              >
                O.M.C. (%)
              </Typography>
              <Typography
                sx={{ gridColumn: "span 1" }}
                textAlign="center"
                variant="subtitle2"
                color="info"
              >
                D.D. (g/cm³)
              </Typography>
              <Typography
                sx={{ gridColumn: "span 1" }}
                textAlign="center"
                variant="subtitle2"
                color="info"
              >
                M.C. (%)
              </Typography>
              <Typography
                sx={{ gridColumn: "span 1" }}
                textAlign="center"
                variant="subtitle2"
                color="info"
              >
                Degree of Compaction
              </Typography>
              <Typography
                sx={{ gridColumn: "span 1" }}
                textAlign="center"
                variant="subtitle2"
                color="info"
              >
                Layer Thickness (mm)
              </Typography>
            </Box>

            <Box
              display="grid"
              gap={0.5}
              padding="2px"
              gridTemplateColumns="repeat(7, minmax(0, 1fr))"
              sx={{
                "& > div": {
                  gridColumn: isNonMobile ? undefined : "span 7",
                },
              }}
            >
              <Typography
                variant="h6"
                textAlign="center"
                alignContent="center"
                sx={{ gridColumn: "span 1" }}
                borderRadius={1}
                border={1}
                color={colors.grey[700]}
              >
                {row.count}.
              </Typography>

              <TextField
                variant="outlined"
                type="number"
                label="M.D.D. (g/cm³)"
                size="small"
                value={row.mdd}
                onChange={(e) =>
                  handleInputChange(row.count, "mdd", e.target.value)
                }
                sx={{
                  gridColumn: "span 1",
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                  },
                }}
              />
              <TextField
                variant="outlined"
                type="number"
                label="O.M.C. (%)"
                size="small"
                value={row.omc}
                onChange={(e) =>
                  handleInputChange(row.count, "omc", e.target.value)
                }
                sx={{
                  gridColumn: "span 1",
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                  },
                }}
              />
              <TextField
                variant="outlined"
                type="number"
                label="D.D. (g/cm³)"
                size="small"
                value={row.dd}
                onChange={(e) =>
                  handleInputChange(row.count, "dd", e.target.value)
                }
                sx={{
                  gridColumn: "span 1",
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                  },
                }}
              />
              <TextField
                variant="outlined"
                type="number"
                label="M.C. (%)"
                size="small"
                value={row.mc}
                onChange={(e) =>
                  handleInputChange(row.count, "mc", e.target.value)
                }
                sx={{
                  gridColumn: "span 1",
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                  },
                }}
              />
              <Typography
                variant="h6"
                textAlign="center"
                alignContent="center"
                sx={{ gridColumn: "span 1" }}
                borderRadius={1}
                border={1}
                color={colors.grey[700]}
              >
                {row.degreeOfCompaction}%
              </Typography>
              <TextField
                variant="outlined"
                type="number"
                label="Layer Thickness (mm)"
                size="small"
                value={row.layerThickness}
                onChange={(e) =>
                  handleInputChange(row.count, "layerThickness", e.target.value)
                }
                sx={{
                  gridColumn: "span 1",
                  "& .MuiInputBase-input": {
                    textAlign: "center",
                  },
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default FieldDensityReportDetails;
